import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { isMobileDevice } from '../utils/isMobileDevice';

const AUDIO_URL = `${process.env.PUBLIC_URL}/joy.mp3`;

const BackgroundAudioContext = createContext(null);

const mobileAudioDefaults = {
  isMuted: true,
  volume: 0,
  showVolumeSlider: false,
  setShowVolumeSlider: () => {},
  toggleMute: () => {},
  handleVolumeChange: () => {},
  isMobileAudioDisabled: true,
};

export const useBackgroundAudio = () => {
  const context = useContext(BackgroundAudioContext);
  if (!context) {
    throw new Error('useBackgroundAudio must be used within BackgroundAudioProvider');
  }
  return context;
};

export const BackgroundAudioProvider = ({ children }) => {
  const [isMobile] = useState(() => isMobileDevice());

  if (isMobile) {
    return (
      <BackgroundAudioContext.Provider value={mobileAudioDefaults}>
        {children}
      </BackgroundAudioContext.Provider>
    );
  }

  return <DesktopBackgroundAudioProvider>{children}</DesktopBackgroundAudioProvider>;
};

const DesktopBackgroundAudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [audioReady, setAudioReady] = useState(false);

  const ensureAudioLoaded = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audioReady) return;

    if (!audio.src) {
      audio.src = AUDIO_URL;
    }
    audio.load();
    setAudioReady(true);
  }, [audioReady]);

  const playAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    ensureAudioLoaded();
    audio.volume = volume;
    audio.loop = true;
    audio.muted = false;

    try {
      await audio.play();
    } catch {
      // Browser blocked autoplay — user can tap unmute again.
    }
  }, [ensureAudioLoaded, volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
  }, [volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = isMuted;
  }, [isMuted]);

  const toggleMute = () => {
    setIsMuted((prev) => {
      const nextMuted = !prev;
      if (!nextMuted) {
        playAudio();
      } else if (audioRef.current) {
        audioRef.current.pause();
      }
      return nextMuted;
    });
  };

  const handleVolumeChange = (newVolume) => {
    const clamped = Math.min(1, Math.max(0, newVolume));
    setVolume(clamped);

    if (clamped === 0) {
      setIsMuted(true);
      if (audioRef.current) {
        audioRef.current.pause();
      }
      return;
    }

    ensureAudioLoaded();
    if (audioRef.current) {
      audioRef.current.volume = clamped;
    }

    if (isMuted) {
      setIsMuted(false);
      playAudio();
    }
  };

  const value = {
    isMuted,
    volume,
    showVolumeSlider,
    setShowVolumeSlider,
    toggleMute,
    handleVolumeChange,
    isMobileAudioDisabled: false,
  };

  return (
    <BackgroundAudioContext.Provider value={value}>
      <audio ref={audioRef} preload="none" playsInline />
      {children}
    </BackgroundAudioContext.Provider>
  );
};
